const fallbackLabels = {
  'games.meta.untitled': 'Untitled game',
  'games.meta.singlePlayer': '{{count}} players',
  'games.meta.playerRange': '{{min}}-{{max}} players',
  'games.meta.players': '{{count}} players',
  'games.meta.playersUnknown': 'Players unknown',
  'games.meta.timeUnknown': 'Time unknown',
  'games.meta.weightUnknown': 'Weight unknown',
  'games.meta.weightLight': 'Light ({{value}})',
  'games.meta.weightMedium': 'Medium ({{value}})',
  'games.meta.weightHeavy': 'Heavy ({{value}})',
  'games.meta.noDescription': 'No description has been written for this game yet.',
};

const fallbackTranslator = (key, optionsOrFallback) => {
  if (typeof optionsOrFallback === 'string') return optionsOrFallback;
  if (
    optionsOrFallback &&
    typeof optionsOrFallback === 'object' &&
    'defaultValue' in optionsOrFallback
  ) {
    return optionsOrFallback.defaultValue;
  }

  const fallback =
    (key === 'games.meta.singlePlayer' || key === 'games.meta.players') &&
    Number(optionsOrFallback?.count) === 1
      ? '{{count}} player'
      : fallbackLabels[key] || key;

  return String(fallback)
    .replace('{{count}}', optionsOrFallback?.count)
    .replace('{{min}}', optionsOrFallback?.min)
    .replace('{{max}}', optionsOrFallback?.max)
    .replace('{{value}}', optionsOrFallback?.value);
};

const normalizeLanguage = (language) =>
  String(language || 'en').split('-')[0].toLowerCase();

const pickLocalizedValue = (value, language, fallback) => {
  const locale = normalizeLanguage(language);

  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value[locale] || value.en || fallback;
  }

  return fallback;
};

export const gameTitle = (game, t = fallbackTranslator, language = 'en') =>
  pickLocalizedValue(
    game?.titleLocales,
    language,
    game?.title || game?.name || t('games.meta.untitled')
  );

export const playerRange = (game, t = fallbackTranslator) => {
  if (game?.minPlayers && game?.maxPlayers) {
    return game.minPlayers === game.maxPlayers
      ? t('games.meta.singlePlayer', {
          count: game.minPlayers,
        })
      : t('games.meta.playerRange', {
          min: game.minPlayers,
          max: game.maxPlayers,
        });
  }

  return game?.players
    ? t('games.meta.players', {
        count: game.players,
      })
    : t('games.meta.playersUnknown');
};

export const playTime = (game, t = fallbackTranslator) => {
  if (game?.playingTime) return `${game.playingTime} min`;
  if (game?.minPlayTime && game?.maxPlayTime) {
    return game.minPlayTime === game.maxPlayTime
      ? `${game.minPlayTime} min`
      : `${game.minPlayTime}-${game.maxPlayTime} min`;
  }

  return game?.duration || t('games.meta.timeUnknown');
};

export const weightLabel = (game, t = fallbackTranslator) => {
  const weight = Number(game?.weight);
  if (!Number.isFinite(weight)) {
    return game?.weight || t('games.meta.weightUnknown');
  }
  if (weight < 2) {
    return t('games.meta.weightLight', {
      value: weight.toFixed(1),
    });
  }
  if (weight < 3.2) {
    return t('games.meta.weightMedium', {
      value: weight.toFixed(1),
    });
  }
  return t('games.meta.weightHeavy', {
    value: weight.toFixed(1),
  });
};

export const gameMetaLine = (game, t = fallbackTranslator) =>
  `${playerRange(game, t)} - ${playTime(game, t)} - ${weightLabel(game, t)}`;

export const compactGameMetaLine = (game, t = fallbackTranslator) =>
  `${playerRange(game, t)} - ${playTime(game, t)}`;

export const bggRatingValue = (game) => {
  const value = Number(game?.bggAverage ?? game?.averageRating ?? game?.average);

  return Number.isFinite(value) && value > 0 ? value : null;
};

export const primaryTags = (game, limit = 3) => {
  const tags = [...(game?.mechanics || []), ...(game?.categories || [])];
  return tags.slice(0, limit);
};

const htmlEntities = {
  amp: '&',
  apos: "'",
  ccedil: 'ç',
  eacute: 'é',
  egrave: 'è',
  ecirc: 'ê',
  euml: 'ë',
  agrave: 'à',
  aacute: 'á',
  acirc: 'â',
  auml: 'ä',
  icirc: 'î',
  iuml: 'ï',
  nbsp: ' ',
  ntilde: 'ñ',
  oacute: 'ó',
  ocirc: 'ô',
  ouml: 'ö',
  quot: '"',
  rsquo: "'",
  lsquo: "'",
  rdquo: '"',
  ldquo: '"',
  ugrave: 'ù',
  uacute: 'ú',
  ucirc: 'û',
  uuml: 'ü',
  szlig: 'ß',
  ndash: '-',
  mdash: '-',
  hellip: '...',
  lt: '<',
  gt: '>',
};

const decodeHtmlEntities = (value) =>
  value
    .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
      String.fromCodePoint(Number.parseInt(hex, 16))
    )
    .replace(/&#(\d+);/g, (_, dec) =>
      String.fromCodePoint(Number.parseInt(dec, 10))
    )
    .replace(/&([a-z]+);/gi, (match, name) => htmlEntities[name.toLowerCase()] || match);

const decodeRepeatedEntities = (value) =>
  decodeHtmlEntities(decodeHtmlEntities(value));

const normalizeDescriptionStructure = (value) =>
  value
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6]|tr)\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '- ')
    .replace(/<[^>]*>/g, ' ');

const languageMarkerRegex =
  /\b(?:french|fran[cç]ais|german|deutsch|spanish|espa[nñ]ol|italian|italiano|portuguese|portugu[eê]s|dutch|nederlands)\s*(?:description|version|rules|publisher)?\s*[:：-]/i;

const splitMultilingualDescription = (value) => {
  const marker = value.match(languageMarkerRegex);
  if (marker?.index && marker.index > 80) {
    return value.slice(0, marker.index).trim();
  }

  return value;
};

const removePublisherBoilerplate = (value) =>
  value.replace(
    /^(?:description\s+from\s+(?:the\s+)?publisher|from\s+the\s+publisher|publisher'?s\s+description)\s*[:：-]\s*/i,
    ''
  );

export const cleanDescription = (description = '') => {
  const structured = normalizeDescriptionStructure(
    decodeRepeatedEntities(String(description))
  );

  const text = structured
    .replace(/\r\n/g, '\n')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .split('\n')
    .map((segment) => segment.trim())
    .filter(Boolean)
    .join(' ')
    .replace(/([.!?])(?=[A-ZÀ-Ö])/g, '$1 ')
    .replace(/\s+/g, ' ')
    .trim();

  return removePublisherBoilerplate(splitMultilingualDescription(text))
    .replace(/\s+/g, ' ')
    .trim();
};

export const shortDescription = (game, t = fallbackTranslator) => {
  const description = cleanDescription(game?.description);

  return (
    description ||
    t('games.meta.noDescription')
  );
};
